/** Partilhar com pessoas: as regras da interface, sem React nem rede.
 *
 *  Os modelos IFC, os BCF sincronizados e as triagens dos conflitos têm
 *  dono e lista de acessos: pessoas (de qualquer workspace, ou
 *  convidadas por email) e/ou o workspace inteiro. Quem decide é o
 *  servidor (backend/core/partilhas_regras.py); isto só decide o que se
 *  mostra. Ver docs/PARTILHA.md.
 *
 *  A sessão ('sessao') junta as três: partilha-se de uma vez e a lista
 *  dela propaga-se às peças (ver core/sessoes).
 */

export type TipoPartilha = 'modelo' | 'bcf' | 'triagem' | 'sessao'

/** Quantos recursos de cada tipo foram partilhados com a pessoa a
 *  partir de outros workspaces (vem do /api/auth/me). */
export type Partilhados = Partial<Record<TipoPartilha, number>>

export interface AcessoDaPartilha {
  id: string
  tipo: 'pessoa' | 'workspace' | 'convite'
  rotulo: string
  email: string | null
  estado: string
  /** O nome da sessão de onde veio este acesso: tira-se lá, não aqui. */
  via_sessao?: string | null
}

/** Uma peça de uma sessão, como a janela a descreve. */
export interface PecaResumo {
  tipo: TipoPartilha
  nome: string | null
}

export interface PartilhaInfo {
  id: string
  tipo: TipoPartilha
  nome: string | null
  chave: string
  workspace_nome: string
  workspace_slug: string
  dono_nome: string | null
  pode_gerir: boolean
  visivel_para: string
  /** A lista completa só chega a quem gere; os outros veem o resumo. */
  acessos: AcessoDaPartilha[]
  workspace_inteiro: boolean
  avisos: string[]
  /** Numa sessão: o que inclui. */
  pecas?: PecaResumo[]
  /** Numa peça (para quem gere): as sessões que a incluem. */
  sessoes?: string[]
  /** Só na resposta de quem acabou de dar acesso: a ligação que o email
   *  leva, para a mandar já por outro meio. */
  ligacoes?: LigacaoDeEntrada[]
}

export interface LigacaoDeEntrada {
  email: string
  url: string
  /** Convite de convidado (entra sem conta, uma vez) ou aviso a quem
   *  já tem conta (a ligação abre a plataforma). */
  convite: boolean
}

/** Junta as ligações novas às que já se mostravam: a mais recente de
 *  cada email ganha (pedir outra anula a anterior). */
export function juntarLigacoes(antes: LigacaoDeEntrada[], novas: LigacaoDeEntrada[] | undefined): LigacaoDeEntrada[] {
  if (!novas || novas.length === 0) return antes
  const emails = new Set(novas.map((l) => l.email))
  return [...antes.filter((l) => !emails.has(l.email)), ...novas]
}

/** O que se diz ao lado de cada ligação. */
export function textoDaLigacao(l: LigacaoDeEntrada): string {
  return l.convite
    ? 'Entra sem conta nem palavra-passe. Serve uma vez, durante 7 dias; pedir outra anula esta.'
    : 'Já tem conta: a ligação abre a plataforma, onde entra com a conta que tem.'
}

export interface ItemComigo {
  id: string
  tipo: TipoPartilha
  nome: string | null
  /** O que o módulo usa para abrir (ver backend/core/partilhas.py). */
  recurso: string
  /** O identificador de projecto de um BCF (o do ficheiro). */
  chave?: string | null
  workspace_nome: string
  dono_nome: string | null
  partilhado_em: string
  /** Vê-se só porque faz parte de uma sessão partilhada (abre-se pela
   *  sessão, e a lista mostra-a lá). */
  so_por_sessao?: boolean
}

export interface PessoaEncontrada {
  user_id: string | null
  email: string
  nome: string | null
  /** Falso para um email escrito por inteiro que não está entre os
   *  conhecidos: a pesquisa não diz se tem conta na plataforma. */
  conhecido: boolean
}

/** O módulo da interface que cada tipo precisa para se abrir. */
export const MODULO_DO_TIPO: Record<TipoPartilha, string> = {
  modelo: 'modelos',
  bcf: 'bcf',
  triagem: 'clash',
  sessao: 'modelos',
}

export const NOME_DO_TIPO: Record<TipoPartilha, string> = {
  modelo: 'Modelo IFC',
  bcf: 'BCF',
  triagem: 'Triagem dos conflitos',
  sessao: 'Sessão',
}

export function totalPartilhados(p: Partilhados | null | undefined): number {
  if (!p) return 0
  return (p.modelo ?? 0) + (p.bcf ?? 0) + (p.triagem ?? 0) + (p.sessao ?? 0)
}

/** O que uma sessão inclui, numa frase ("2 modelos IFC, a triagem dos
 *  conflitos "Cha139" e 1 BCF"). Vazio quando não inclui nada. */
export function resumoDasPecas(pecas: readonly PecaResumo[]): string {
  const modelos = pecas.filter((p) => p.tipo === 'modelo').length
  const bcf = pecas.filter((p) => p.tipo === 'bcf').length
  const triagens = pecas.filter((p) => p.tipo === 'triagem')
  const partes: string[] = []
  if (modelos) partes.push(`${modelos} modelo${modelos === 1 ? '' : 's'} IFC`)
  if (triagens.length === 1) partes.push(`a triagem dos conflitos "${triagens[0].nome ?? 'sem nome'}"`)
  else if (triagens.length > 1) partes.push(`${triagens.length} triagens dos conflitos`)
  if (bcf) partes.push(`${bcf} BCF`)
  if (partes.length <= 1) return partes.join('')
  return `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`
}

/** As partes da interface a mostrar: os módulos atribuídos e, para quem
 *  tem recursos partilhados de outros workspaces, as páginas de que
 *  precisa para os abrir (o BCF e os Conflitos). Não dá acesso a nada no
 *  servidor: lá, cada pedido verifica o recurso. */
export function modulosNaInterface(
  modules: readonly string[],
  partilhados: Partilhados | null | undefined,
): string[] {
  const saida = new Set(modules)
  if ((partilhados?.bcf ?? 0) > 0) saida.add('bcf')
  if ((partilhados?.triagem ?? 0) > 0) saida.add('clash')
  return [...saida]
}

/** Só com acesso por partilha (sem o módulo atribuído): a interface
 *  esconde o que criaria trabalho novo no módulo. */
export function soPorPartilha(
  slug: string,
  modules: readonly string[],
  partilhados: Partilhados | null | undefined,
): boolean {
  return !modules.includes(slug) && modulosNaInterface(modules, partilhados).includes(slug)
}

const EMAIL = /^[^@\s]{1,64}@[^@\s]{1,253}\.[^@\s.]{2,63}$/

export function emailValido(valor: string): string | null {
  const v = valor.trim().toLowerCase()
  return EMAIL.test(v) ? v : null
}

/** Os avisos de privacidade da janela "Partilhar com...", por tipo. Ditos
 *  antes de partilhar e sempre visíveis na janela. */
export function avisosDePrivacidade(tipo: TipoPartilha): string[] {
  const comuns = [
    'Quem tem acesso vê o nome de quem partilhou. A lista completa de acessos só a vê quem partilhou.',
    'Retirar um acesso tem efeito imediato: os pedidos seguintes são recusados e as ligações ao vivo dessa pessoa fecham.',
  ]
  if (tipo === 'modelo') {
    return [
      'O ficheiro IFC fica guardado no servidor da plataforma, comprimido.',
      'Quem descarregou mantém a cópia no browser (e no disco, se a guardou): retirar o acesso não a apaga.',
      ...comuns,
      'Retirar a partilha não apaga o ficheiro do servidor; para isso use "Apagar do servidor".',
    ]
  }
  if (tipo === 'bcf') {
    return [
      'Os tópicos e comentários ficam no servidor e sincronizam com quem tem acesso. Os modelos não sobem.',
      'Quem já sincronizou mantém a cópia no browser: retirar o acesso não a apaga.',
      ...comuns,
    ]
  }
  if (tipo === 'sessao') {
    return [
      'Os modelos abertos que ainda não estão no servidor sobem agora (comprimidos, contam para o espaço do workspace). A triagem dos conflitos e os BCF ficam partilhados.',
      'Quem recebe a sessão ganha acesso a todas estas peças, e perde-o quando o tirar daqui. Os acessos que as peças já tinham por si não mudam.',
      'Quem já abriu a sessão mantém as cópias no browser: retirar o acesso não as apaga.',
      ...comuns,
    ]
  }
  return [
    'A triagem (estados, responsáveis, notas, grupos, classificação) fica no servidor. Os modelos IFC não sobem.',
    'Quem já abriu a triagem mantém a cópia no browser: retirar o acesso não a apaga.',
    ...comuns,
  ]
}

/** O texto da confirmação de "Retirar a partilha". Numa sessão diz o
 *  que sai com ela; numa peça, de que sessões sai também. */
export function textoDeRetirar(
  tipo: TipoPartilha,
  nome: string | null,
  extra: { pecas?: readonly PecaResumo[]; sessoes?: readonly string[] } = {},
): string {
  const coisa = `${NOME_DO_TIPO[tipo].toLowerCase()} "${nome ?? 'sem nome'}"`
  if (tipo === 'sessao') {
    const inclui = resumoDasPecas(extra.pecas ?? [])
    return (
      `Retirar a partilha da sessão "${nome ?? 'sem nome'}"?\n\n`
      + 'Todas as pessoas e o workspace perdem de imediato o acesso à sessão'
      + (inclui ? ` e ao que ela incluía: ${inclui}` : '')
      + '. Os acessos que essas peças já tinham dado por si (fora da sessão) ficam como estão. '
      + 'Nada é apagado: os modelos, a triagem e os BCF continuam no servidor e no seu browser, '
      + 'só para si. Quem já abriu a sessão mantém as cópias no browser.'
    )
  }
  const dados =
    tipo === 'modelo'
      ? 'O ficheiro continua guardado no servidor, só para si; para o apagar use "Apagar do servidor".'
      : 'Os dados continuam no servidor e no seu browser, só para si.'
  const sessoes = extra.sessoes ?? []
  const tambem =
    sessoes.length > 0
      ? ` Sai também da${sessoes.length === 1 ? ' sessão' : 's sessões'} ${sessoes
          .map((s) => `"${s}"`)
          .join(', ')}: quem a recebeu deixa de ver ${tipo === 'triagem' ? 'esta triagem' : `este ${tipo === 'bcf' ? 'BCF' : 'modelo'}`}.`
      : ''
  return (
    `Retirar a partilha de ${coisa}?\n\n`
    + 'Todas as pessoas e o workspace perdem o acesso de imediato: os pedidos seguintes '
    + 'são recusados e as ligações ao vivo fecham.'
    + `${tambem} ${dados} Quem já descarregou ou sincronizou mantém a cópia no browser.`
  )
}

/** "Visível para" para quem gere, a partir da lista completa. */
export function visivelPara(info: Pick<PartilhaInfo, 'acessos' | 'visivel_para' | 'pode_gerir'>): string {
  if (!info.pode_gerir) return info.visivel_para
  if (info.acessos.length === 0) return 'Visível para: só si.'
  // A mesma pessoa pode estar por si e por uma sessão: aparece uma vez.
  const nomes = [
    ...new Set(
      info.acessos.map((a) => (a.tipo === 'convite' ? `${a.rotulo} (${a.estado})` : a.rotulo)),
    ),
  ]
  return `Visível para: ${nomes.join(', ')}.`
}

/** Os itens de "Partilhados comigo" de um tipo, os mais recentes
 *  primeiro. Sem tipo (a lista geral), as sessões vêm primeiro e as
 *  peças que só se veem por uma sessão não aparecem soltas: abrem-se
 *  pela sessão. */
export function doTipo(lista: readonly ItemComigo[], tipo: TipoPartilha | null): ItemComigo[] {
  return lista
    .filter((i) => (tipo === null ? !i.so_por_sessao : i.tipo === tipo))
    .slice()
    .sort((a, b) =>
      tipo === null && (a.tipo === 'sessao') !== (b.tipo === 'sessao')
        ? a.tipo === 'sessao'
          ? -1
          : 1
        : b.partilhado_em.localeCompare(a.partilhado_em),
    )
}

/** Quantas peças da lista só se veem por uma sessão (para a lista geral
 *  dizer que estão dentro das sessões). */
export function pecasSoPorSessao(lista: readonly ItemComigo[]): number {
  return lista.filter((i) => i.so_por_sessao).length
}

/** De onde vem um item, numa linha. */
export function origemDoItem(i: Pick<ItemComigo, 'workspace_nome' | 'dono_nome'>): string {
  return i.dono_nome
    ? `de ${i.dono_nome}, workspace ${i.workspace_nome}`
    : `do workspace ${i.workspace_nome}`
}

/** O identificador com que um BCF partilhado de outro workspace se
 *  sincroniza (ver `projeto_bcf_de_partilha` no backend). */
export function projetoDaPartilha(partilhaId: string): string {
  return `partilha:${partilhaId}`
}
